import { useAuth } from "@clerk/nextjs";
import { useQuery, useMutation, UseQueryResult, useQueryClient } from "@tanstack/react-query";
import { CustomerEmailLocation } from "~/lib/drizzle/type";
import { apiBaseUrl, fetchWrapper } from "~/utils/utils";
import { BaseProps } from "./base";

export const useGetCustomerEmailLocations = (email: string): UseQueryResult<CustomerEmailLocation[]> => {
    const { getToken } = useAuth();
    const queryFn = async () => {
        const token = await getToken();
        const result = await fetchWrapper<CustomerEmailLocation[]>({
            url: `${apiBaseUrl}/customer-emails/locations?email=${encodeURIComponent(email)}`,
            method: 'GET',
            token
        });

        return result
    }

    return useQuery({
        queryKey: ['getCustomerEmailLocations', email],
        queryFn,
        enabled: !!email
    })
}

export const useLinkCustomerEmailLocation = (props: BaseProps) => {
    const { onSuccess, onError } = props;
    const { getToken } = useAuth();
    const queryClient = useQueryClient();

    const mutationFn = async (payload: { email: string, locationIds: string[] }) => {
        const token = await getToken();
        const result = await fetchWrapper({
            url: `${apiBaseUrl}/customer-emails/locations`,
            method: 'PUT',
            token,
            body: {
                email: payload.email,
                location_ids: payload.locationIds
            }
        });

        return result
    }

    const mutation = useMutation({
        mutationFn,
        onSuccess: async (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['getCustomerEmailLocations', variables.email] });
            onSuccess?.(data, variables);
        },
        onError: (error) => {
            onError?.(error);
        }
    })

    return mutation;
}

export const useUnlinkCustomerEmailLocation = (props: BaseProps) => {
    const { onSuccess, onError } = props;
    const { getToken } = useAuth();
    const queryClient = useQueryClient();

    const mutationFn = async (payload: { email: string, locationIds: string[] }) => {
        const token = await getToken();
        const result = await fetchWrapper({
            url: `${apiBaseUrl}/customer-emails/locations`,
            method: 'DELETE',
            token,
            body: {
                email: payload.email,
                location_ids: payload.locationIds
            }
        });

        return result
    }

    const mutation = useMutation({
        mutationFn,
        onSuccess: async (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['getCustomerEmailLocations', variables.email] });
            onSuccess?.(data, variables);
        },
        onError: (error) => {
            onError?.(error);
        }
    })

    return mutation;
}
